import {gql} from '@apollo/client';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LinkerPayloadLeavingGroupsQuery} from 'molbio/adcs/linker-payload-connection/__generated/LinkerPayloadLeavingGroupsQuery';

export const LINKER_PAYLOAD_INACCESSIBLE_ERROR = "You don't have access to the linker-payload.";
export const LINKER_PAYLOAD_LEAVING_GROUPS_LOOKUP_ERROR =
  "Couldn't look up the linker-payload's leaving groups.";

export const LINKER_PAYLOAD_LEAVING_GROUPS_QUERY = gql`
  query LinkerPayloadLeavingGroupsQuery($moleculeId: StrictString!) @engComponent(name: MOLBIO) @skipAudit {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        name
        currentVersion {
          id
          chemicalStructure {
            id
            canonicalSmiles
            leavingGroups {
              ... on LeavingGroupsNode {
                numbers
              }
              ... on ErrorNode {
                message
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface LeavingGroupWithSmiles {
  number: number;
  smiles: string | null;
}

export type LinkerPayloadLeavingGroupsResult =
  | {leavingGroups: Array<LeavingGroupWithSmiles>}
  | {error: string};

interface DemoLinkerPayload {
  moleculeName: string;
  canonicalSmiles: string;
  /** The SMILES of the molecule's leaving groups, by number. */
  smilesByLeavingGroup: Readonly<Record<number, string>>;
}

/** Demo linker-payloads whose leaving groups have hardcoded SMILES, matched by name or canonical SMILES. */
export const DEMO_LINKER_PAYLOADS: ReadonlyArray<DemoLinkerPayload> = [
  {
    moleculeName: 'NHS-adipoyl-Val-Cit-PAB-MMAE',
    canonicalSmiles:
      'CC[C@H](C)[C@@H]([C@@H](CC(=O)N1CCC[C@H]1[C@H](OC)[C@@H](C)C(=O)N[C@H](C)[C@@H](O)C1=CC=CC=C1)OC)N(C)C(=O)[C@@H](NC(=O)[C@H](C(C)C)N(C)C(=O)OCC1=CC=C(NC(=O)[C@H](CCCNC(N)=O)NC(=O)[C@@H](NC(=O)CCCCC(=O)ON2C(=O)CCC2=O)C(C)C)C=C1)C(C)C',
    smilesByLeavingGroup: {2: 'ON1C(=O)CCC1=O'},
  },
];

/**
 * Reads the leaving groups drawn on a linker-payload molecule's current structure, by number in ascending order,
 * with their SMILES where known (see DEMO_LINKER_PAYLOADS).
 */
export function getLinkerPayloadLeavingGroupsResult({
  molecule,
}: LinkerPayloadLeavingGroupsQuery): LinkerPayloadLeavingGroupsResult {
  if (isInaccessible(molecule)) {
    return {error: LINKER_PAYLOAD_INACCESSIBLE_ERROR};
  }
  const {leavingGroups} = molecule.currentVersion.chemicalStructure;
  if (leavingGroups.__typename === 'ErrorNode') {
    return {error: leavingGroups.message};
  }

  const {canonicalSmiles} = molecule.currentVersion.chemicalStructure;
  const demoLinkerPayload = DEMO_LINKER_PAYLOADS.find(
    (demo) => demo.moleculeName === molecule.name || demo.canonicalSmiles === canonicalSmiles
  );
  return {
    leavingGroups: leavingGroups.numbers.map((number) => ({
      number,
      smiles: demoLinkerPayload?.smilesByLeavingGroup[number] ?? null,
    })),
  };
}
